import type { DropOffLocation } from '../../../../../core/api/locator/models/locator.model';

/** i18n namespace for nearest-location Available Services labels. */
export const AVAILABLE_SERVICE_I18N_PREFIX =
  'createShipment.serviceStep.pickupDropOff.nearestLocation.services' as const;

type CapabilityField = keyof Pick<
  DropOffLocation,
  | 'labelFreeService'
  | 'dropBoxesOnsitePurolator'
  | 'kiosk'
  | 'packageFreeService'
  | 'additionalHandling'
  | 'outboundShippingPrepaid'
  | 'outboundShippingPayCounter'
  | 'streetAccess'
  | 'parcelLockersOnsitePurolator'
  | 'holdForPickup'
  | 'dangerousGoods'
  | 'heavyweight'
  | 'wheelChairAccess'
  | 'parkingAccess'
  | 'suppliesavailable'
>;

/**
 * Lead-table order: each row is a capability field and the i18n key suffix to show when true.
 * `labelFreeService` maps to the Label-free / QR code label only.
 */
const AVAILABLE_SERVICE_ROWS: readonly { field: CapabilityField; keySuffix: string }[] = [
  { field: 'dropBoxesOnsitePurolator', keySuffix: 'purolatorDropBox' },
  { field: 'kiosk', keySuffix: 'purolatorSelfServe' },
  { field: 'packageFreeService', keySuffix: 'packageFreeQr' },
  { field: 'labelFreeService', keySuffix: 'labelFreeQr' },
  { field: 'additionalHandling', keySuffix: 'specialHandling' },
  { field: 'outboundShippingPrepaid', keySuffix: 'outboundShippingPrepaid' },
  { field: 'outboundShippingPayCounter', keySuffix: 'outboundShippingPayCounter' },
  { field: 'streetAccess', keySuffix: 'streetAccess' },
  { field: 'parcelLockersOnsitePurolator', keySuffix: 'parcelPoint' },
  { field: 'holdForPickup', keySuffix: 'holdForPickup' },
  { field: 'dangerousGoods', keySuffix: 'dangerousGoods' },
  { field: 'heavyweight', keySuffix: 'heavyweight' },
  { field: 'wheelChairAccess', keySuffix: 'wheelchairAccess' },
  { field: 'parkingAccess', keySuffix: 'parkingAvailable' },
  { field: 'suppliesavailable', keySuffix: 'packagingSupplies' },
];

/**
 * Builds Available Services i18n keys for a locator location in lead-table order.
 * Only truthy capability flags are included.
 *
 * @returns Ordered i18n keys, or an empty array when location is null or all flags are false
 */
export function mapAvailableServiceKeys(location: DropOffLocation | null): string[] {
  if (!location) {
    return [];
  }

  return AVAILABLE_SERVICE_ROWS.filter(({ field }) => location[field] === true).map(
    ({ keySuffix }) => `${AVAILABLE_SERVICE_I18N_PREFIX}.${keySuffix}`,
  );
}
